<template>
  <div class="reading-activity-page">
    <header class="page-header">
      <div>
        <h2>User Reading Activity</h2>
        <p>Inspect card-level reading events across LearningFirst experiences.</p>
      </div>
      <el-button size="small" icon="el-icon-refresh" :loading="loading" @click="fetchActivity">
        Refresh
      </el-button>
    </header>

    <section class="filter-bar" aria-label="Reading activity filters">
      <div class="filter-main">
        <label class="filter-field date-field">
          <span>Date range</span>
          <el-date-picker
            v-model="filters.dateRange"
            type="daterange"
            range-separator="to"
            start-placeholder="Start date"
            end-placeholder="End date"
            value-format="yyyy-MM-dd"
            :picker-options="datePickerOptions"
            :clearable="false"
            @change="handleFilterChange"
          />
        </label>

        <label class="filter-field scene-field">
          <span>Reading surface</span>
          <el-select v-model="filters.readScene" @change="search">
            <el-option label="All surfaces" value="all" />
            <el-option label="Collection Reader" value="collection" />
            <el-option label="Inbox" value="inbox" />
            <el-option label="Post Detail" value="post_detail" />
            <el-option label="Legacy Post" value="post" />
          </el-select>
        </label>

        <label class="filter-field search-field">
          <span>User</span>
          <el-input
            v-model.trim="filters.search"
            clearable
            placeholder="Email, name, or user ID"
            @clear="search"
            @keyup.enter.native="search"
          >
            <el-button slot="append" icon="el-icon-search" aria-label="Search" @click="search" />
          </el-input>
        </label>
      </div>

      <div class="filter-actions">
        <div class="quick-ranges" aria-label="Quick date ranges">
          <button
            v-for="preset in rangePresets"
            :key="preset.days"
            type="button"
            :class="{ active: activeRangeDays === preset.days }"
            @click="setRange(preset.days)"
          >
            {{ preset.label }}
          </button>
        </div>
        <el-button size="small" @click="resetFilters">Reset</el-button>
        <el-button size="small" type="primary" icon="el-icon-search" @click="search">Search</el-button>
      </div>
    </section>

    <el-alert
      v-if="errorMessage"
      :title="errorMessage"
      type="error"
      show-icon
      :closable="false"
      class="query-error"
    />

    <section class="summary-strip" aria-label="Reading activity summary">
      <div class="summary-item primary-summary">
        <span>Read events</span>
        <strong>{{ formatNumber(summary.read_events) }}</strong>
        <small>Every card visit, including repeat reads</small>
      </div>
      <div class="summary-item">
        <span>Readers</span>
        <strong>{{ formatNumber(summary.readers) }}</strong>
        <small>Distinct users in this result</small>
      </div>
      <div class="summary-item">
        <span>Sessions</span>
        <strong>{{ formatNumber(summary.sessions) }}</strong>
        <small>Distinct learning posts opened</small>
      </div>
      <div class="summary-item range-summary">
        <span>Coverage</span>
        <strong>{{ rangeLabel }}</strong>
        <small>{{ range.days || 0 }} calendar days</small>
      </div>
    </section>

    <section class="activity-table-section">
      <div class="table-heading">
        <div>
          <h3>Activity log</h3>
          <p>Newest events first · timestamps use the server timezone</p>
        </div>
        <span v-if="!loading" class="result-count">{{ formatNumber(total) }} results</span>
      </div>

      <el-table
        v-loading="loading"
        :data="items"
        size="small"
        class="activity-table"
        row-key="row_key"
      >
        <el-table-column label="Time" width="176">
          <template slot-scope="scope">
            <time class="event-time" :datetime="isoDate(scope.row.read_time)">
              {{ formatDateTime(scope.row.read_time) }}
            </time>
          </template>
        </el-table-column>

        <el-table-column label="User" min-width="250">
          <template slot-scope="scope">
            <div class="user-cell">
              <span class="user-initial" aria-hidden="true">{{ userInitial(scope.row) }}</span>
              <div class="user-copy">
                <strong>{{ scope.row.user_name || scope.row.user_email || 'Unknown user' }}</strong>
                <span v-if="scope.row.user_name && scope.row.user_email">{{ scope.row.user_email }}</span>
                <el-tooltip :content="scope.row.user_id || 'No user ID'" placement="top" :open-delay="350">
                  <span class="technical-id">{{ compactId(scope.row.user_id) }}</span>
                </el-tooltip>
              </div>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Learning content" min-width="300">
          <template slot-scope="scope">
            <div class="content-cell">
              <strong :title="scope.row.collection_title || ''">
                {{ scope.row.collection_title || 'Collection unavailable' }}
              </strong>
              <span :title="scope.row.post_title || ''">
                {{ scope.row.post_title || 'Session unavailable' }}
              </span>
              <span class="technical-id">{{ compactId(scope.row.post_id) }}</span>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Card" width="174">
          <template slot-scope="scope">
            <div class="card-cell">
              <el-tag size="mini" type="info">{{ cardTypeLabel(scope.row.card_type) }}</el-tag>
              <span>Card {{ Number(scope.row.card_index || 0) + 1 }}</span>
              <el-tooltip v-if="scope.row.card_code" :content="scope.row.card_code" placement="top" :open-delay="350">
                <span class="technical-id">{{ compactId(scope.row.card_code, 7) }}</span>
              </el-tooltip>
            </div>
          </template>
        </el-table-column>

        <el-table-column label="Surface" width="150">
          <template slot-scope="scope">
            <el-tag size="small" :type="sceneTagType(scope.row.read_scene)" effect="plain">
              {{ sceneLabel(scope.row.read_scene) }}
            </el-tag>
          </template>
        </el-table-column>

        <el-table-column label="Access" width="118">
          <template slot-scope="scope">
            <span class="method-label">{{ methodLabel(scope.row.view_method) }}</span>
          </template>
        </el-table-column>

        <template slot="empty">
          <div class="empty-state">
            <span class="empty-icon"><i class="el-icon-reading"></i></span>
            <strong>No reading activity found</strong>
            <p>Try a wider date range or clear the user and surface filters.</p>
          </div>
        </template>
      </el-table>

      <div class="pagination-row">
        <el-pagination
          background
          layout="total, sizes, prev, pager, next"
          :current-page.sync="page"
          :page-size.sync="pageSize"
          :page-sizes="[20, 50, 100]"
          :total="total"
          @size-change="handleSizeChange"
          @current-change="fetchActivity"
        />
      </div>
    </section>
  </div>
</template>

<script>
import dayjs from 'dayjs'
import { listUserReadingActivity } from '@/apis/lfAdmin'

function defaultDateRange(days = 30) {
  return [
    dayjs().subtract(days - 1, 'day').format('YYYY-MM-DD'),
    dayjs().format('YYYY-MM-DD')
  ]
}

export default {
  name: 'LFAdminReadingActivity',
  data() {
    return {
      loading: false,
      errorMessage: '',
      items: [],
      total: 0,
      page: 1,
      pageSize: 20,
      requestSequence: 0,
      summary: {
        read_events: 0,
        readers: 0,
        sessions: 0
      },
      range: {
        date_from: '',
        date_to: '',
        days: 0
      },
      filters: {
        dateRange: defaultDateRange(),
        readScene: 'all',
        search: ''
      },
      rangePresets: [
        { label: '7D', days: 7 },
        { label: '30D', days: 30 },
        { label: '90D', days: 90 }
      ],
      datePickerOptions: {
        disabledDate(date) {
          return date.getTime() > dayjs().endOf('day').valueOf()
        }
      }
    }
  },
  computed: {
    activeRangeDays() {
      const range = this.filters.dateRange
      if (!range || range.length !== 2) return 0
      const start = dayjs(range[0]).startOf('day')
      const end = dayjs(range[1]).startOf('day')
      return end.diff(start, 'day') + 1
    },
    rangeLabel() {
      if (!this.range.date_from || !this.range.date_to) return '—'
      return `${dayjs(this.range.date_from).format('MMM D')} – ${dayjs(this.range.date_to).format('MMM D')}`
    }
  },
  mounted() {
    this.fetchActivity()
  },
  methods: {
    handleFilterChange() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return
      const days = this.activeRangeDays
      if (days > 366) {
        this.$message.warning('Choose a date range of 366 days or less')
        return
      }
      this.search()
    },
    setRange(days) {
      this.filters.dateRange = defaultDateRange(days)
      this.search()
    },
    resetFilters() {
      this.filters = {
        dateRange: defaultDateRange(),
        readScene: 'all',
        search: ''
      }
      this.search()
    },
    search() {
      if (this.activeRangeDays > 366) {
        this.$message.warning('Choose a date range of 366 days or less')
        return
      }
      this.page = 1
      this.fetchActivity()
    },
    handleSizeChange() {
      this.page = 1
      this.fetchActivity()
    },
    async fetchActivity() {
      if (!this.filters.dateRange || this.filters.dateRange.length !== 2) return
      const sequence = ++this.requestSequence
      this.loading = true
      this.errorMessage = ''
      try {
        const response = await listUserReadingActivity({
          date_from: this.filters.dateRange[0],
          date_to: this.filters.dateRange[1],
          read_scene: this.filters.readScene,
          search: this.filters.search,
          page: this.page,
          page_size: this.pageSize
        })
        if (sequence !== this.requestSequence) return
        if (!response.data || Number(response.data.code) !== 0) {
          throw new Error((response.data && response.data.msg) || 'Failed to load reading activity')
        }

        const data = response.data.data || {}
        const rows = Array.isArray(data.items) ? data.items : []
        this.items = rows.map((row, index) => ({
          ...row,
          row_key: `${row.user_id || 'unknown'}-${row.post_id || 'unknown'}-${row.read_time || 0}-${row.card_code || row.card_index || 0}-${index}`
        }))
        this.total = Number(data.total || 0)
        this.summary = {
          read_events: Number((data.summary && data.summary.read_events) || 0),
          readers: Number((data.summary && data.summary.readers) || 0),
          sessions: Number((data.summary && data.summary.sessions) || 0)
        }
        this.range = data.range || {
          date_from: this.filters.dateRange[0],
          date_to: this.filters.dateRange[1],
          days: this.activeRangeDays
        }

        const lastPage = Math.max(1, Math.ceil(this.total / this.pageSize))
        if (this.page > lastPage) {
          this.page = lastPage
          this.fetchActivity()
        }
      } catch (error) {
        if (sequence !== this.requestSequence) return
        this.items = []
        this.total = 0
        this.summary = { read_events: 0, readers: 0, sessions: 0 }
        this.errorMessage = error.message || 'Failed to load reading activity'
      } finally {
        if (sequence === this.requestSequence) this.loading = false
      }
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US')
    },
    formatDateTime(timestamp) {
      const value = Number(timestamp || 0)
      return value > 0 ? dayjs(value * 1000).format('YYYY-MM-DD HH:mm:ss') : '—'
    },
    isoDate(timestamp) {
      const value = Number(timestamp || 0)
      return value > 0 ? dayjs(value * 1000).toISOString() : ''
    },
    compactId(value, visible = 6) {
      const text = String(value || '')
      if (!text) return 'No ID'
      if (text.length <= visible * 2 + 1) return text
      return `${text.slice(0, visible)}…${text.slice(-visible)}`
    },
    userInitial(row) {
      const source = String(row.user_name || row.user_email || '?').trim()
      return source ? source.charAt(0).toUpperCase() : '?'
    },
    sceneLabel(value) {
      const scene = String(value || '').toLowerCase()
      const labels = {
        collection: 'Collection Reader',
        inbox: 'Inbox',
        post_detail: 'Post Detail',
        post: 'Legacy Post'
      }
      return labels[scene] || 'Other'
    },
    sceneTagType(value) {
      const scene = String(value || '').toLowerCase()
      if (scene === 'collection') return 'primary'
      if (scene === 'inbox') return 'success'
      if (scene === 'post_detail' || scene === 'post') return 'warning'
      return 'info'
    },
    methodLabel(value) {
      const method = String(value || '').toUpperCase()
      const labels = {
        COLLECTION: 'Collection',
        FREE: 'Free',
        TOKEN: 'Token',
        USD: 'Paid',
        EXCHANGE: 'Exchange'
      }
      return labels[method] || method || 'Unknown'
    },
    cardTypeLabel(value) {
      const type = String(value || '').toLowerCase()
      const labels = {
        text: 'Text',
        quiz: 'Quiz',
        image: 'Image',
        video: 'Video',
        youtube: 'YouTube',
        quote: 'Quote',
        blockquote: 'Quote',
        summary: 'Summary',
        ending: 'Ending',
        cover: 'Cover',
        key_terms: 'Key terms'
      }
      return labels[type] || 'Unknown'
    }
  }
}
</script>

<style scoped lang="scss">
.reading-activity-page {
  --admin-blue: #1159af;
  --admin-blue-soft: #edf4fc;
  --ink: #1f2d3d;
  --muted: #66758a;
  --line: #e2e8f0;
  min-width: 920px;
  padding: 24px 26px 36px;
  color: var(--ink);
}

.page-header,
.table-heading,
.filter-main,
.filter-actions,
.user-cell {
  display: flex;
  align-items: center;
}

.page-header {
  justify-content: space-between;
  margin-bottom: 20px;
}

.page-header h2,
.table-heading h3 {
  margin: 0;
  color: #172436;
  font-weight: 650;
}

.page-header h2 {
  font-size: 22px;
  letter-spacing: -0.02em;
}

.page-header p,
.table-heading p {
  margin: 5px 0 0;
  color: var(--muted);
  font-size: 13px;
}

.filter-bar {
  padding: 18px 20px 15px;
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 4px;
}

.filter-main {
  align-items: flex-end;
  gap: 16px;
}

.filter-field {
  display: flex;
  flex-direction: column;
  gap: 7px;
}

.filter-field > span {
  color: #526176;
  font-size: 12px;
  font-weight: 600;
}

.date-field {
  width: 350px;
}

.scene-field {
  width: 190px;
}

.search-field {
  flex: 1;
  min-width: 250px;
}

.date-field ::v-deep .el-date-editor,
.scene-field ::v-deep .el-select,
.search-field ::v-deep .el-input {
  width: 100%;
}

.filter-actions {
  justify-content: flex-end;
  gap: 8px;
  padding-top: 14px;
  margin-top: 14px;
  border-top: 1px solid #edf0f4;
}

.quick-ranges {
  display: inline-flex;
  margin-right: auto;
  padding: 2px;
  background: #f1f4f8;
  border-radius: 5px;
}

.quick-ranges button {
  min-width: 48px;
  padding: 6px 10px;
  color: #65748a;
  font: inherit;
  font-size: 12px;
  font-weight: 600;
  background: transparent;
  border: 0;
  border-radius: 3px;
  cursor: pointer;
  transition: color 160ms ease, background 160ms ease;
}

.quick-ranges button:hover {
  color: var(--admin-blue);
}

.quick-ranges button.active {
  color: #0d4d9a;
  background: #ffffff;
  box-shadow: 0 1px 2px rgba(25, 42, 70, 0.12);
}

.query-error {
  margin-top: 16px;
}

.summary-strip {
  display: grid;
  grid-template-columns: 1.2fr 1fr 1fr 1.35fr;
  margin: 20px 0;
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 4px;
}

.summary-item {
  position: relative;
  min-width: 0;
  padding: 17px 20px 16px;
}

.summary-item + .summary-item::before {
  position: absolute;
  top: 16px;
  bottom: 16px;
  left: 0;
  width: 1px;
  background: var(--line);
  content: '';
}

.summary-item span,
.summary-item small {
  display: block;
}

.summary-item span {
  color: #627187;
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.045em;
}

.summary-item strong {
  display: block;
  margin-top: 7px;
  color: #172436;
  font-size: 25px;
  line-height: 1;
  letter-spacing: -0.025em;
}

.primary-summary strong {
  color: var(--admin-blue);
}

.summary-item small {
  margin-top: 8px;
  overflow: hidden;
  color: #8793a4;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.range-summary strong {
  font-size: 18px;
  line-height: 25px;
}

.activity-table-section {
  overflow: hidden;
  background: #ffffff;
  border: 1px solid var(--line);
  border-radius: 4px;
}

.table-heading {
  justify-content: space-between;
  padding: 17px 20px 15px;
  border-bottom: 1px solid var(--line);
}

.table-heading h3 {
  font-size: 15px;
}

.result-count {
  color: #68778b;
  font-size: 12px;
  font-weight: 600;
}

.activity-table::before {
  display: none;
}

.activity-table ::v-deep th.el-table__cell {
  padding: 10px 0;
  color: #526176;
  font-size: 11px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.035em;
  background: #f7f9fc;
}

.activity-table ::v-deep td.el-table__cell {
  padding: 12px 0;
  border-bottom-color: #edf0f4;
}

.activity-table ::v-deep .el-table__row:hover > td.el-table__cell {
  background: #f7faff;
}

.event-time {
  color: #334155;
  font-size: 12px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.user-cell {
  gap: 10px;
  min-width: 0;
}

.user-initial {
  display: inline-flex;
  flex: 0 0 32px;
  width: 32px;
  height: 32px;
  align-items: center;
  justify-content: center;
  color: #14569f;
  font-size: 13px;
  font-weight: 700;
  background: var(--admin-blue-soft);
  border-radius: 50%;
}

.user-copy,
.content-cell,
.card-cell {
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: 3px;
}

.user-copy strong,
.content-cell strong {
  overflow: hidden;
  color: #233146;
  font-size: 13px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.user-copy > span,
.content-cell > span,
.card-cell > span {
  overflow: hidden;
  color: #738096;
  font-size: 11px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.technical-id {
  color: #99a3b1 !important;
  font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  font-size: 10px !important;
  cursor: help;
}

.card-cell ::v-deep .el-tag {
  align-self: flex-start;
}

.method-label {
  color: #526176;
  font-size: 12px;
}

.empty-state {
  display: flex;
  min-height: 250px;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  color: #78869a;
}

.empty-icon {
  display: inline-flex;
  width: 48px;
  height: 48px;
  margin-bottom: 12px;
  align-items: center;
  justify-content: center;
  color: #4e79a8;
  font-size: 22px;
  background: #eef4fb;
  border-radius: 50%;
}

.empty-state strong {
  color: #3d4b60;
  font-size: 14px;
}

.empty-state p {
  margin: 5px 0 0;
  font-size: 12px;
}

.pagination-row {
  display: flex;
  justify-content: flex-end;
  padding: 16px 20px;
  border-top: 1px solid var(--line);
}

@media (max-width: 1180px) {
  .reading-activity-page {
    padding-right: 20px;
    padding-left: 20px;
  }

  .filter-main {
    flex-wrap: wrap;
  }

  .search-field {
    flex-basis: 100%;
  }

  .summary-strip {
    grid-template-columns: repeat(2, 1fr);
  }

  .summary-item:nth-child(3)::before {
    display: none;
  }

  .summary-item:nth-child(n + 3) {
    border-top: 1px solid var(--line);
  }
}
</style>
